import type { ReactNode } from "react";
import {
  cellFrontOpening,
  flapDownProblem,
  flapUpProblem,
  LIFT_SPECS,
  liftSpec,
  liftTypesFor,
  liftUnavailableReason,
  resolvedLiftType,
  singleDoorProblem,
  type CellFrontOpening,
  type LiftType
} from "../domain/flapFronts";
import { mezzanineFlapHeightMm } from "../domain/moduleRules";
import type { ShelvingCellConfig, WardrobeModule } from "../types";

type PictogramKind = CellFrontOpening | LiftType | "doors";

/**
 * Схемы открывания (10.10): подъёмники — вид сбоку (корпус слева, фасад справа),
 * двери и откидные — вид спереди. Чтобы было видно, чем отличаются HL, HS, HF и HK.
 */
function FrontPictogram({ kind }: { kind: PictogramKind }) {
  const carcass = <rect x="5" y="12" width="22" height="17" rx="1" className="front-pictogram__carcass" />;
  const shapes: Record<PictogramKind, ReactNode> = {
    left: <><rect x="11" y="5" width="18" height="22" rx="1" /><path d="M13.5 9v3M13.5 20v3" className="front-pictogram__accent" /></>,
    right: <><rect x="11" y="5" width="18" height="22" rx="1" /><path d="M26.5 9v3M26.5 20v3" className="front-pictogram__accent" /></>,
    double: <><rect x="7" y="5" width="12.5" height="22" rx="1" /><rect x="20.5" y="5" width="12.5" height="22" rx="1" /></>,
    doors: <><rect x="7" y="5" width="12.5" height="22" rx="1" /><rect x="20.5" y="5" width="12.5" height="22" rx="1" /></>,
    up: <><rect x="7" y="13" width="26" height="14" rx="1" /><path d="M20 10V3M17 6l3-3 3 3" className="front-pictogram__accent" /></>,
    down: <><rect x="7" y="5" width="26" height="14" rx="1" /><path d="M20 22v7M17 26l3 3 3-3" className="front-pictogram__accent" /></>,
    // Сбоку: HL — фасад стоит вертикально над корпусом; HS — лежит сверху;
    // HF — сложен «книжкой» спереди; HK — повёрнут вверх на оси у верхней кромки.
    HL: <>{carcass}<path d="M30 2v9" className="front-pictogram__front" /><path d="M35.5 10V4M33.5 6l2-2 2 2" className="front-pictogram__accent" /></>,
    HS: <>{carcass}<path d="M10 9h23" className="front-pictogram__front" /><path d="M36 24c1.5-6 .5-10.5-3-13.5" className="front-pictogram__accent" /></>,
    HF: <>{carcass}<path d="M28.5 12l8.5-2.5-8-4.5" className="front-pictogram__front" /></>,
    HK: <>{carcass}<path d="M28.5 12l8-8.5" className="front-pictogram__front" /><path d="M36.5 25c2-4.5 2-8.5.5-12" className="front-pictogram__accent" /></>
  };
  return <svg className="front-pictogram" viewBox="0 0 40 32" aria-hidden="true">{shapes[kind]}</svg>;
}

/** Подъёмник для «вверх»: подобран по высоте фасада и месту сверху, можно сменить на другой подходящий. */
export function LiftTypePicker({ chosen, heightMm, headroomMm = Infinity, excluded = [], onChange }: {
  chosen: LiftType | undefined;
  heightMm: number;
  /** Свободно над фасадом, мм: 0 — сверху ячейка или антресоль. */
  headroomMm?: number;
  /** Подъёмники, которых у изделия нет совсем (у комода — HS): их не показываем. */
  excluded?: readonly LiftType[];
  onChange: (lift: LiftType | undefined) => void;
}) {
  const specs = LIFT_SPECS.filter((spec) => !excluded.includes(spec.type));
  const fitting = liftTypesFor(heightMm, headroomMm, excluded);
  const current = resolvedLiftType(chosen, heightMm, headroomMm, excluded);
  const unavailable = specs.flatMap((spec) => liftUnavailableReason(spec.type, heightMm, headroomMm) ?? []);
  return <div className="front-lift-picker">
    <span className="field-caption">Подъёмник</span>
    <div className={`front-opening-picker__options ${specs.length === 4 ? "is-four" : "is-three"}`} role="group" aria-label="Тип подъёмника">
      {specs.map((spec) => {
        const reason = liftUnavailableReason(spec.type, heightMm, headroomMm);
        return <button type="button" key={spec.type} disabled={Boolean(reason)}
          className={current === spec.type ? "is-selected" : ""} aria-pressed={current === spec.type}
          title={reason ?? spec.hint}
          onClick={() => onChange(spec.type === fitting[0] ? undefined : spec.type)}>
          <FrontPictogram kind={spec.type} />
          <span>{spec.type}</span>
        </button>;
      })}
    </div>
    <small className="sl-help">
      {current ? `${liftSpec(current).title}: ${liftSpec(current).hint}. ` : ""}
      {chosen && chosen === current ? "Выбран вручную." : `Подобран по высоте фасада ${Math.round(heightMm)} мм.`}
    </small>
    {unavailable.length > 0 && <ul className="front-opening-picker__reasons">
      {unavailable.map((reason) => <li key={reason}>{reason}.</li>)}
    </ul>}
  </div>;
}

const CELL_OPENINGS: [CellFrontOpening, string][] = [
  ["left", "Петли слева"],
  ["right", "Петли справа"],
  ["up", "Откидной вверх"],
  ["down", "Откидной вниз"],
  ["double", "Две дверцы"]
];
const MIN_DOUBLE_WIDTH_MM = 300;

/**
 * Как открывается фасад ячейки (09.10): дверца слева или справа, две дверцы
 * или один откидной фасад вверх (подъёмник Aventos) или вниз. Распашная
 * одна дверца — до 600 мм.
 */
export function CellFrontOpeningPicker({ cell, widthMm, heightMm, headroomMm = Infinity, excludedLifts = [], onPatch }: {
  cell: ShelvingCellConfig;
  widthMm: number;
  heightMm: number;
  /** Свободно над фасадом, мм (shelvingCellHeadroomMm): 0 — сверху другая ячейка. */
  headroomMm?: number;
  /** Подъёмники, которых у изделия нет (shelvingExcludedLifts). */
  excludedLifts?: readonly LiftType[];
  onPatch: (patch: Partial<ShelvingCellConfig>) => void;
}) {
  const opening = cellFrontOpening(cell);
  const reasons: Record<CellFrontOpening, string | null> = {
    left: singleDoorProblem(widthMm),
    right: singleDoorProblem(widthMm),
    double: widthMm < MIN_DOUBLE_WIDTH_MM ? `Для двух дверец проём нужен от ${MIN_DOUBLE_WIDTH_MM} мм` : null,
    up: flapUpProblem(heightMm, headroomMm, excludedLifts),
    down: flapDownProblem(heightMm)
  };
  const select = (value: CellFrontOpening) => onPatch(value === "left" || value === "right"
    ? { doorOpening: undefined, doorHinge: value }
    : { doorOpening: value });
  const effective = (opening === "up" || opening === "down") && reasons[opening] ? "left" : opening;
  const disabled = CELL_OPENINGS.filter(([value]) => reasons[value] && value !== effective);
  return <div className="front-opening-picker">
    <span className="field-caption">Как открывается фасад</span>
    <div className="front-opening-picker__options" role="group" aria-label="Как открывается фасад">
      {CELL_OPENINGS.map(([value, label]) => <button type="button" key={value}
        className={effective === value ? "is-selected" : ""} aria-pressed={effective === value}
        disabled={Boolean(reasons[value]) && effective !== value} title={reasons[value] ?? undefined}
        onClick={() => select(value)}><FrontPictogram kind={value} /><span>{label}</span></button>)}
    </div>
    {(effective === "left" || effective === "right") && reasons.left &&
      <p className="soft-note">Фасад {Math.round(widthMm)} мм: одна дверца шире 600 мм тяжела для петель и провисает. Выберите две дверцы или откидной фасад.</p>}
    {effective === "up" && <LiftTypePicker chosen={cell.doorLift} heightMm={heightMm} headroomMm={headroomMm} excluded={excludedLifts} onChange={(doorLift) => onPatch({ doorLift })} />}
    {effective === "down" && <small className="sl-help">Фасад на петлях внизу откидывается до горизонтали и держится на складных кронштейнах с доводчиком по бокам.</small>}
    {disabled.length > 0 && <ul className="front-opening-picker__reasons">
      {disabled.map(([value, label]) => <li key={value}>«{label}»: {reasons[value]!.charAt(0).toLowerCase()}{reasons[value]!.slice(1)}.</li>)}
    </ul>}
  </div>;
}

/** Антресоль: двери или по откидному фасаду на пролёт — вверх или вниз (09.10). */
export function MezzanineOpeningPicker({ module, headroomMm = Infinity, onPatch }: {
  module: WardrobeModule;
  /** Место до потолка над антресолью, мм (moduleHeadroomMm). */
  headroomMm?: number;
  onPatch: (patch: Partial<WardrobeModule>) => void;
}) {
  const heightMm = mezzanineFlapHeightMm(module);
  const upProblem = flapUpProblem(heightMm, headroomMm);
  const downProblem = flapDownProblem(heightMm);
  const current = module.doorOpening === "up" && !upProblem ? "up" : module.doorOpening === "down" && !downProblem ? "down" : "doors";
  const options: ["doors" | "up" | "down", string, string | null][] = [
    ["doors", "Двери", null],
    ["up", "Откидной вверх", upProblem],
    ["down", "Откидной вниз", downProblem]
  ];
  return <div className="front-opening-picker">
    <span className="eyebrow">Как открывается антресоль</span>
    <div className="front-opening-picker__options is-three" role="group" aria-label="Как открывается антресоль">
      {options.map(([value, label, reason]) => <button type="button" key={value}
        className={current === value ? "is-selected" : ""} aria-pressed={current === value}
        disabled={Boolean(reason)} title={reason ?? undefined}
        onClick={() => onPatch({ doorOpening: value === "doors" ? undefined : value })}><FrontPictogram kind={value} /><span>{label}</span></button>)}
    </div>
    {current === "up" && <LiftTypePicker chosen={module.doorLift} heightMm={heightMm} headroomMm={headroomMm} onChange={(doorLift) => onPatch({ doorLift })} />}
    <small className="sl-help">{current === "doors"
      ? "Пролёт шире 600 мм закрывают две дверцы. Вместо них можно поставить один откидной фасад на пролёт."
      : current === "up" ? "В каждом пролёте — один фасад на подъёмнике, петли не нужны (у складного HF — между частями)."
        : "В каждом пролёте — один фасад на петлях внизу, откидывается до горизонтали на складных кронштейнах."}</small>
    {[upProblem, downProblem].some(Boolean) && <ul className="front-opening-picker__reasons">
      {[upProblem, downProblem].flatMap((reason) => reason ?? []).map((reason) => <li key={reason}>{reason}.</li>)}
    </ul>}
  </div>;
}
